import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import {
  Flame, MessageSquare, CheckCircle2, XCircle, Search, RefreshCw,
  ChevronRight, Sparkles, AlertTriangle,
} from "lucide-react";
import {
  IntentBadge, StatusBadge, SOURCE_LABEL, BU_LABEL, relativeTime,
} from "../../components/OpportunityBadges";

const STATUS_FILTERS = [
  { key: "", label: "Todas" },
  { key: "NEW", label: "Novas" },
  { key: "CONTACTING", label: "Em atendimento" },
  { key: "CONVERTED", label: "Convertidas" },
  { key: "LOST", label: "Não convertidas" },
];
const INTENT_RANK = { HIGH: 3, MEDIUM: 2, LOW: 1, NONE: 0 };
const INTENT_FILTERS = [
  { key: "", label: "Todas" },
  { key: "HIGH", label: "Alta" },
  { key: "MEDIUM", label: "Média" },
  { key: "LOW", label: "Baixa" },
];

function KpiCard({ icon: Icon, label, value, cls, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-border bg-white p-3 sm:p-4 shadow-card">
      <div className={`grid h-9 w-9 place-items-center rounded-xl ${cls}`}>
        <Icon size={18} />
      </div>
      <div className="mt-2 text-2xl font-bold text-brand-dark tabular-nums">{value ?? 0}</div>
      <div className="text-[11px] font-medium uppercase tracking-wide text-[#8A7A88]">{label}</div>
    </div>
  );
}

function OpportunityCard({ opp, onOpen }) {
  const bu = BU_LABEL[opp.business_unit] || opp.business_unit;
  const interest = (opp.product_text || opp.original_message || "Interesse não detalhado").trim();
  return (
    <button
      type="button"
      onClick={() => onOpen(opp)}
      data-testid={`opp-card-${opp.id}`}
      className="w-full rounded-2xl border border-border bg-white p-4 text-left shadow-card transition-all hover:border-brand/40 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand/40"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-bold text-brand-dark">
            {opp.customer_name || "Cliente sem nome"}
          </div>
          <div className="mt-1 line-clamp-2 text-sm text-[#6B5A69]">{interest}</div>
        </div>
        <StatusBadge status={opp.status} />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <IntentBadge level={opp.intent_level} />
        <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-[11px] font-medium text-[#6B5A69]">
          📲 {SOURCE_LABEL[opp.source] || opp.source}
        </span>
        {opp.business_unit && opp.business_unit !== "UNKNOWN" && (
          <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-[11px] font-medium text-[#6B5A69]">
            {bu}
          </span>
        )}
      </div>
      <div className="mt-3 flex items-center justify-between border-t border-border/60 pt-2">
        <span className="text-[11px] text-[#8A7A88]">{relativeTime(opp.last_message_at || opp.created_at)}</span>
        {opp.overdue ? (
          <span data-testid="opp-health-overdue" className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-bold text-red-700">⚠️ Aguardando atendimento</span>
        ) : opp.health === "ATTENTION" ? (
          <span data-testid="opp-health-attention" className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700">🟡 Atenção</span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand">Ver oportunidade <ChevronRight size={13} /></span>
        )}
      </div>
    </button>
  );
}

export default function OpportunitiesPage() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [status, setStatus] = useState("");
  const [intent, setIntent] = useState("");
  const [q, setQ] = useState("");
  const [onlyAttention, setOnlyAttention] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const params = {};
      if (status) params.status = status;
      if (intent) params.intent_level = intent;
      if (q.trim()) params.q = q.trim();
      const [listRes, statsRes] = await Promise.all([
        api.get("/opportunities", { params }),
        api.get("/opportunities/stats").catch(() => ({ data: null })),
      ]);
      setRows(listRes.data || []);
      setStats(statsRes.data);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [status, intent, q]);

  useEffect(() => { load(); }, [status, intent]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = setTimeout(() => load(), 400);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const openOpp = (opp) => nav(`/revenda/oportunidades/${opp.id}`);

  const displayRows = React.useMemo(() => {
    const tier = (o) => (o.overdue ? 2 : o.health === "ATTENTION" ? 1 : 0);
    let r = onlyAttention ? rows.filter((o) => o.overdue || o.health === "ATTENTION") : rows.slice();
    r.sort((a, b) => (tier(b) - tier(a))
      || ((INTENT_RANK[b.intent_level] || 0) - (INTENT_RANK[a.intent_level] || 0))
      || (new Date(b.last_message_at || b.created_at) - new Date(a.last_message_at || a.created_at)));
    return r;
  }, [rows, onlyAttention]);

  return (
    <div className="mx-auto max-w-5xl px-1 pb-24 sm:px-0" data-testid="opportunities-page">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-brand-dark">
            <Flame className="text-brand" size={24} /> Oportunidades
          </h1>
          <p className="mt-0.5 text-sm text-[#6B5A69]">Clientes que demonstraram interesse pelo WhatsApp oficial.</p>
        </div>
        <button
          onClick={load}
          data-testid="opp-refresh-btn"
          aria-label="Atualizar"
          className="grid h-10 w-10 place-items-center rounded-xl border border-border bg-white text-brand hover:bg-muted"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {/* KPIs */}
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiCard icon={Flame} label="Novas" value={stats?.new} cls="bg-brand/10 text-brand" testid="opp-kpi-new" />
        <KpiCard icon={MessageSquare} label="Em atendimento" value={stats?.contacting} cls="bg-amber-100 text-amber-700" testid="opp-kpi-contacting" />
        <KpiCard icon={CheckCircle2} label="Convertidas" value={stats?.converted} cls="bg-emerald-100 text-emerald-700" testid="opp-kpi-converted" />
        <KpiCard icon={XCircle} label="Não convertidas" value={stats?.lost} cls="bg-red-100 text-red-700" testid="opp-kpi-lost" />
      </div>

      {/* Filtros */}
      <div className="mb-3 space-y-2">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A7A88]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por cliente ou interesse…"
            data-testid="opp-search-input"
            className="w-full rounded-xl border border-border bg-white py-2.5 pl-9 pr-3 text-sm text-brand-dark focus:border-brand focus:outline-none"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="opp-status-filters">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key || "all"}
              onClick={() => setStatus(f.key)}
              data-testid={`opp-filter-status-${f.key || "all"}`}
              className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                status === f.key ? "bg-brand-grad text-white" : "border border-border bg-white text-[#6B5A69] hover:bg-muted"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="opp-intent-filters">
          {INTENT_FILTERS.map((f) => (
            <button
              key={f.key || "all"}
              onClick={() => setIntent(f.key)}
              data-testid={`opp-filter-intent-${f.key || "all"}`}
              className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                intent === f.key ? "bg-brand text-white" : "border border-border bg-white text-[#6B5A69] hover:bg-muted"
              }`}
            >
              {f.label}
            </button>
          ))}
          <button onClick={() => setOnlyAttention((v) => !v)} data-testid="opp-filter-attention"
            className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${onlyAttention ? "bg-red-500 text-white" : "border border-border bg-white text-[#6B5A69] hover:bg-muted"}`}>
            ⚠️ Precisa de atenção
          </button>
        </div>
      </div>

      {/* Conteudo */}
      {loading ? (
        <div className="space-y-3" data-testid="opp-loading">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-28 animate-pulse rounded-2xl bg-muted" />
          ))}
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center" data-testid="opp-error">
          <AlertTriangle className="mx-auto text-red-500" size={28} />
          <p className="mt-2 text-sm font-medium text-red-700">Não foi possível carregar suas oportunidades.</p>
          <button onClick={load} data-testid="opp-retry-btn" className="mt-3 rounded-xl bg-brand-grad px-4 py-2 text-sm font-semibold text-white">
            Tentar novamente
          </button>
        </div>
      ) : displayRows.length === 0 ? (
        <div className="rounded-2xl border border-border bg-white p-10 text-center shadow-card" data-testid="opp-empty">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-brand/10 text-brand">
            <Sparkles size={26} />
          </div>
          <p className="mt-3 text-base font-semibold text-brand-dark">💜 Nenhuma oportunidade por aqui ainda.</p>
          <p className="mt-1 text-sm text-[#6B5A69]">
            Quando uma cliente demonstrar interesse pelo WhatsApp oficial, ela aparecerá aqui.
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="opp-list">
          {displayRows.map((opp) => (
            <OpportunityCard key={opp.id} opp={opp} onOpen={openOpp} />
          ))}
        </div>
      )}
    </div>
  );
}
